import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { XCircle, VideoOff } from 'lucide-react';
import { cn } from '@companion-professional/webutils';
import { useMediaRecorder } from './useMediaRecorder';
import { RecordingControls } from './RecordingControls';
import { CameraCaptureStates, VideoRecordingStats } from './inBrowserCameraTypes';
import { DeviceSettings } from './DeviceSettings';
import { VideoTime } from './VideoTime';
import { LoaderSpinner } from '../LoaderSpinner';
import { PreviewControls } from './PreviewControls';
import { Button } from '../Button';
import { useScreenOrientation } from './useScreenOrientation';
import { Heading, Text } from '../Typography';

export { type VideoRecordingStats };

// TODO: For some reason, the video is not showing up (i.e. is black) when the checkup app is launched using the IOS
//  QR code scanner.  We should investigate why this is happening and fix it.  NOTE: scanning the QR code with the
//  camera app works fine.

interface InBrowserCameraProps {
  // onUploadVideo is a function that is called when the user clicks the "Upload" button.  It is passed the File that
  // was created from the finalBlob along with the stats of the video that was recorded.
  onUploadVideo?: (file: File, stats: VideoRecordingStats) => void;

  // onBack is a function that is called when the user clicks the "Back" button (e.g. it can be used to navigate back
  // to the previous screen).
  onBack?: () => void;

  // showErrorScreen is a boolean that determines whether to show the error screen.  This is useful for testing the
  // error state of the component (i.e. looking at the error state in Storybook).
  showErrorScreen?: boolean;

  // onError is a function that is called if an error occurs.  It is passed the error message.
  onError?: (error: string) => void;

  // id is a unique identifier for the instance of in-browser-camera. In near future,
  // it will just be checkup id.
  id: string;

  warningImage?: string;

  emitBrowserCameraMetrics?: (properties: Record<string, unknown>) => void;
}

// InBrowserCamera is a component that allows the user to record a video (using their camera) and then upload it.
export const InBrowserCamera: FunctionComponent<InBrowserCameraProps> = ({
  onUploadVideo = () => {},
  onBack = () => {},
  onError = () => {},
  showErrorScreen = false,
  warningImage = '',
  emitBrowserCameraMetrics = () => {},
  id
}) => {
  const {
    videoRef,
    finalBlobRef,
    cameraCaptureState,
    recordingStartTime,
    recordingStopTime,
    error,
    devices,
    startCamera,
    stopCamera,
    startRecording,
    stopRecording
  } = useMediaRecorder();
  const [videoDeviceId, setVideoDeviceId] = useState<string | undefined>();
  const [showShortVideoWarning, setShowShortVideoWarning] = useState(false);
  const [showLandscapeWarning, setShowLandscapeWarning] = useState(false);

  const orientation = useScreenOrientation();

  useEffect(() => {
    setShowLandscapeWarning(false);
  }, [orientation]);

  const numberOfShortVideosCaptured = useRef(0);
  const numberOfTimesVideosCaptureClicked = useRef(0);

  useEffect(() => {
    stopCamera();
    startCamera(videoDeviceId).catch((err) => {
      console.error('Error starting camera', err);
    });
  }, [videoDeviceId]);

  useEffect(() => {
    // This will pass an error message coming from the useMediaRecorder hook to the parent component.
    if (error) {
      onError(error);
    }
  }, [error]);

  const doUploadVideo = () => {
    if (!finalBlobRef.current) {
      console.error('Cannot submit recording without a blob');
      return;
    }

    const properties = {
      id: id,
      number_of_short_videos_recorded: numberOfShortVideosCaptured.current,
      number_of_times_videos_capture_clicked: numberOfTimesVideosCaptureClicked.current,
      duration: (recordingStopTime || 0) - (recordingStartTime || 0)
    };

    emitBrowserCameraMetrics(properties);

    const createdFile = new File([finalBlobRef.current], 'video-from-camera', { type: finalBlobRef.current.type });
    onUploadVideo(createdFile, {
      size: createdFile.size,
      type: createdFile.type,
      duration: (recordingStopTime || 0) - (recordingStartTime || 0),
      width: videoRef.current?.videoWidth,
      height: videoRef.current?.videoHeight
    });
  };

  const submitRecording = () => {
    const duration = (recordingStopTime || 0) - (recordingStartTime || 0);

    if (duration < 30000) {
      setShowShortVideoWarning(true);
      numberOfShortVideosCaptured.current++;
      return;
    }

    doUploadVideo();
  };

  if (error || showErrorScreen) {
    // TODO: Add more specific/helpful error handling https://app.clickup.com/t/86898w0vt
    return (
      <div className="flex w-full flex-1 flex-col items-center justify-center gap-4 text-destructive">
        <VideoOff size="48" />
        <Text size="lg" weight="strong" className="m-6">
          Something went wrong accessing your camera.
        </Text>
        <Button
          onClick={() => {
            stopCamera();
            onBack();
          }}
          size="small"
          variant="primary"
          className="ml-2"
        >
          Back
        </Button>
      </div>
    );
  }

  // TODO: The bottom-[15%] class is used to position our extra recording and preview controls at the bottom of the
  //  video preview, but above the video controls.  A possible better way of doing this would be to make a custom video
  //  player component that includes the recording controls.  That way we'd have complete control over the layout and
  //  styling of the video player and recording controls.
  return (
    <div className="flex w-full flex-1 flex-col items-center justify-center object-contain">
      {cameraCaptureState === CameraCaptureStates.LOADING ? (
        <div className="flex flex-1 items-center justify-center">
          <LoaderSpinner size="large" />
        </div>
      ) : null}

      {showShortVideoWarning && (
        <>
          {/* Dark overlay that makes everything unclickable */}
          <div className="absolute inset-0 z-40 bg-brand-navy/60" />

          {/* Alert with buttons */}
          <div className="absolute left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-xs -translate-x-1/2 -translate-y-1/2 transform rounded-2xl border bg-widget px-3 py-4 text-on-widget shadow-popover sm:max-w-sm md:max-w-md">
            <div className="text-center">
              <Heading level="subsection" as="div" className="mb-2">
                Video Too Short
              </Heading>
              <div className="mb-3 text-xs sm:mb-4 sm:text-sm">
                We expect video to be at least <strong>30 seconds</strong> long. Would you like to record again?
              </div>
              <div className="flex flex-col justify-center gap-2 sm:flex-row">
                <Button
                  onClick={() => {
                    setShowShortVideoWarning(false);
                    stopCamera();
                    startCamera(videoDeviceId);
                  }}
                  variant="primary"
                  className="order-1"
                >
                  YES, Record Again
                </Button>
                <Button
                  onClick={() => {
                    setShowShortVideoWarning(false);
                    doUploadVideo();
                  }}
                  variant="ghost"
                  size="small"
                  className="order-2 self-center"
                >
                  No, Upload Anyway
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {showLandscapeWarning && (
        <>
          <div className="absolute left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-xs -translate-x-1/2 -translate-y-1/2 transform rounded-2xl border bg-widget px-3 py-4 text-on-widget shadow-popover sm:max-w-sm md:max-w-md">
            <div className="text-center">
              <Heading level="subsection" as="div" className="mb-2">
                Videos should be in landscape mode
              </Heading>
              <div className="mb-3 text-xs sm:mb-4 sm:text-sm">
                We prefer landscape videos, please rotate your device.
              </div>
              <img src={warningImage} alt="Landscape Warning" className="h-auto w-full object-contain" />
            </div>
          </div>
        </>
      )}

      <div
        className={cn('relative flex flex-1 flex-row items-center justify-center', {
          hidden: cameraCaptureState === CameraCaptureStates.LOADING
        })}
      >
        <div className="relative">
          <video ref={videoRef} autoPlay playsInline muted className="relative" />
          <RecordingControls
            className="absolute bottom-[15%] left-0 right-0 flex flex-col items-center justify-end"
            cameraCaptureState={cameraCaptureState}
            startRecording={() => {
              if (orientation === 'landscape') {
                // This is where the video capture button is clicked.
                numberOfTimesVideosCaptureClicked.current++;
                startRecording();
              } else {
                // if it's portrait, show the landscape warning, making users to rotate their device.
                setShowLandscapeWarning(true);
              }
            }}
            stopRecording={stopRecording}
          >
            <VideoTime recordingStartTime={recordingStartTime} />

            <DeviceSettings
              devices={devices}
              onVideoDeviceIdChange={setVideoDeviceId}
              currentVideoDeviceId={videoDeviceId}
            />

            <XCircle
              size={30}
              color="white"
              className="hover:bg-secondary"
              onClick={() => {
                stopCamera();
                onBack();
              }}
            />
          </RecordingControls>

          <PreviewControls
            className="absolute bottom-[15%] left-0 right-0 flex flex-col items-center justify-end"
            cameraCaptureState={cameraCaptureState}
            submitRecording={submitRecording}
            startOver={async () => {
              stopCamera();
              await startCamera(videoDeviceId);
            }}
          />
        </div>
      </div>
    </div>
  );
};
